import { shuffle, createCardInstance, getEffectiveScale } from './gameUtils.js';
import { getPlayerName, getUILoc } from '../utils/cardUtils.js';
import { processEffects } from './effectHandler.js';
import { produce } from "immer";
import { _updateGameOverState } from './gameOver.js';
import {
    INITIAL_HAND_SIZE,
    BASE_CONSCIOUSNESS,
    FIRST_PLAYER_CONSCIOUSNESS_MODIFIER,
    INITIAL_SCALE,
    INITIAL_FIELD_LIMIT,
    MAX_HAND_SIZE,
    PlayerId,
    GamePhase,
    TriggerType,
    EffectType,
    HUMAN_PLAYER_ID,
    NPC_PLAYER_ID
} from './constants.js';

/**
 * Checks if the game is over and sets the winner.
 */
export const checkGameOver = (gameState) => produce(gameState, _updateGameOverState);

/**
 * Initializes the entire game state for a new game.
 */
export const initializeGame = (cardDefs, presetDecks, player1DeckSource, player2DeckSource, tutorialScenario = null, roguelikeParams = null, hypothesisParams = null) => {
    // 引数が文字列なら名前で検索（互換性維持）、オブジェクトならそのまま使用
    const p1DeckObject = (typeof player1DeckSource === 'string') 
        ? presetDecks.find(deck => deck.name === player1DeckSource)
        : player1DeckSource;
        
    const p2DeckObject = (typeof player2DeckSource === 'string')
        ? presetDecks.find(deck => deck.name === player2DeckSource)
        : player2DeckSource;

    if (!p1DeckObject) {
        throw new Error(`Player 1 deck not found: ${player1DeckSource?.name || player1DeckSource}`);
    }
    if (!p2DeckObject) {
        throw new Error(`Player 2 deck not found: ${player2DeckSource?.name || player2DeckSource}`);
    }

    const findTemplate = (idOrName) => {
        // IDで直接ルックアップ
        if (cardDefs[idOrName]) return cardDefs[idOrName];
        
        // 名前でルックアップ（App.jsでインジェクトされた日本語名を使用）
        const byName = Object.values(cardDefs).find(def => def.name === idOrName);
        
        if (!byName) {
            // エラーの詳細を投げ、App.jsのcatchブロックで捕捉して表示する
            throw new Error(`カード「${idOrName}」が見つかりませんでした。古いセーブデータをお持ちの場合は、一度ランをリセットしてください。`);
        }
        return byName;
    };

    const p1DeckTemplates = p1DeckObject.cards.map(findTemplate);
    const p2DeckTemplates = p2DeckObject.cards.map(findTemplate);

    let p1Deck = p1DeckTemplates.map(t => createCardInstance(t, PlayerId.PLAYER1));
    let p2Deck = p2DeckTemplates.map(t => createCardInstance(t, PlayerId.PLAYER2));

    // シャッフル設定の解決 (仮説検証 または チュートリアル または ローグライク)
    // プレイヤーとNPCで個別に制御可能にする
    const shuffleP1 = hypothesisParams 
        ? (hypothesisParams.shufflePlayerDeck ?? false) 
        : (roguelikeParams?.shufflePlayerDeck ?? (tutorialScenario?.shuffleDeck ?? true));
    const shuffleP2 = hypothesisParams 
        ? (hypothesisParams.shuffleNPCDeck ?? false) 
        : (roguelikeParams?.shuffleNPCDeck ?? (tutorialScenario?.shuffleDeck ?? true));

    if (shuffleP1) {
        p1Deck = shuffle(p1Deck);
        console.log('[GAME] Player 1 deck shuffled.');
    } else {
        console.log('[GAME] Player 1 deck shuffling skipped.');
    }

    if (shuffleP2) {
        p2Deck = shuffle(p2Deck);
        console.log('[GAME] Player 2 deck shuffled.');
    } else {
        console.log('[GAME] Player 2 deck shuffling skipped.');
    }

    const p1HandSize = hypothesisParams?.playerInitialHandSize ?? tutorialScenario?.playerInitialHandSize ?? roguelikeParams?.playerInitialHandSize ?? INITIAL_HAND_SIZE;
    const p2HandSize = hypothesisParams?.npcInitialHandSize ?? tutorialScenario?.npcInitialHandSize ?? roguelikeParams?.npcInitialHandSize ?? INITIAL_HAND_SIZE;
    const p1Hand = p1Deck.splice(0, p1HandSize);
    const p2Hand = p2Deck.splice(0, p2HandSize);

    // 初期ステータスの決定 (ベース値 15/5 + ボーナス / 仮説検証直接設定)
    const p1Con = hypothesisParams?.playerConsciousness !== undefined
        ? hypothesisParams.playerConsciousness
        : ((roguelikeParams && roguelikeParams.playerConsciousnessBonus !== undefined)
            ? 15 + roguelikeParams.playerConsciousnessBonus 
            : (p1DeckObject.consciousness !== undefined ? 15 + p1DeckObject.consciousness : BASE_CONSCIOUSNESS));

    const p1Scale = hypothesisParams?.playerScale !== undefined
        ? hypothesisParams.playerScale
        : ((roguelikeParams && roguelikeParams.playerScaleBonus !== undefined)
            ? 5 + roguelikeParams.playerScaleBonus
            : (p1DeckObject.scale !== undefined ? 5 + p1DeckObject.scale : INITIAL_SCALE));

    const p2Con = hypothesisParams?.npcConsciousness !== undefined
        ? hypothesisParams.npcConsciousness
        : ((roguelikeParams && roguelikeParams.npcConsciousnessBonus !== undefined)
            ? 15 + roguelikeParams.npcConsciousnessBonus
            : (p2DeckObject.consciousness !== undefined ? 15 + p2DeckObject.consciousness : BASE_CONSCIOUSNESS));
    const p2Scale = hypothesisParams?.npcScale !== undefined
        ? hypothesisParams.npcScale
        : ((roguelikeParams && roguelikeParams.npcScaleBonus !== undefined)
            ? 5 + roguelikeParams.npcScaleBonus
            : (p2DeckObject.scale !== undefined ? 5 + p2DeckObject.scale : INITIAL_SCALE));

    let gameState = {
        players: {
            [HUMAN_PLAYER_ID]: {
                id: HUMAN_PLAYER_ID,
                name: getPlayerName('SELF'),
                isNPC: false,
                consciousness: p1Con,
                scale: p1Scale,
                field_limit: INITIAL_FIELD_LIMIT,
                deck: p1Deck,
                hand: p1Hand,
                field: [],
                discard: [],
                ideology: null,
                hand_capacity: MAX_HAND_SIZE,
                modify_parameter_corrections: [],
                cards_played_this_turn: 0,
                yocan_drawn_card_ids: [],
                redevelopment_used: false,
                exposed_cards: [],
            },
            [NPC_PLAYER_ID]: {
                id: NPC_PLAYER_ID,
                name: getPlayerName('OPPONENT'),
                isNPC: true,
                consciousness: p2Con,
                scale: p2Scale,
                field_limit: INITIAL_FIELD_LIMIT,
                deck: p2Deck,
                hand: p2Hand,
                field: [],
                discard: [],
                ideology: null,
                hand_capacity: MAX_HAND_SIZE,
                modify_parameter_corrections: [],
                cards_played_this_turn: 0,
                yocan_drawn_card_ids: [],
                redevelopment_used: false,
                exposed_cards: [],
            }
        },
        bgmPack:p2DeckObject.bgmPack ?? 'game', // デフォルトは 'game'、存在しない場合は 'game' にフォールバック
        current_turn: null,
        // マリガン設定 (プレイヤーとNPCで個別に制御可能にする)
        phase: (hypothesisParams?.enableMulligan === false || tutorialScenario?.enableMulligan === false || (roguelikeParams && (roguelikeParams.enablePlayerMulligan === false && roguelikeParams.enableNPCMulligan === false))) ? GamePhase.START_TURN : GamePhase.MULLIGAN,
        isRoguelikeMode: !hypothesisParams && !!roguelikeParams,
        isHypothesisMode: !!hypothesisParams,
        hypothesisParams: hypothesisParams,
        roguelikeParams: roguelikeParams,
        mulligan_state: {
            [HUMAN_PLAYER_ID]: { status: (hypothesisParams || roguelikeParams?.enablePlayerMulligan === false) ? 'decided' : 'undecided', count: 0 },
            [NPC_PLAYER_ID]: { status: (hypothesisParams || roguelikeParams?.enableNPCMulligan === false) ? 'decided' : 'undecided', count: 0 },
        },
        effect_queue: [],
        awaiting_input: null,
        effects_to_skip: {},
        game_over: false,
        winner: null,
        temp_effect_data: {},
        displaced_ideology: null,
        all_card_instances: {},
        cardDefs: cardDefs,
        turn_number: 1,
        round_number: 1,
        turn_in_round: 1,
        game_log: [],
        turn_end_state: 'ready_for_next_turn',
        processing_status: {
            is_processing_turn_end: false,
            effects_remaining: 0,
            awaiting_input_for: null,
            pending_turn_transition: false
        },
        firstTurnStarted: false,
        lastUpdate: null,
        isAnimationLocked: false,
        animation_queue: [],
        turnHistory: [],
        initial_decks: {
            [HUMAN_PLAYER_ID]: {
                name: p1DeckObject.name,
                cards: p1DeckTemplates.map(t => t.id)
            },
            [NPC_PLAYER_ID]: {
                name: p2DeckObject.name,
                cards: p2DeckTemplates.map(t => t.id)
            }
        },
    };

    Object.values(gameState.players).forEach(player => {
        player.deck.forEach(card => {
            card.location = 'deck'; // SET LOCATION
            gameState.all_card_instances[card.instance_id] = card;
        });
        player.hand.forEach(card => {
            card.location = 'hand'; // SET LOCATION
            gameState.all_card_instances[card.instance_id] = card;
        });
    });

    // チュートリアルまたは仮説検証設定に基づく先攻後攻の決定
    const playerIds = [PlayerId.PLAYER1, PlayerId.PLAYER2];
    let firstPlayerId;
    
    const specifiedFirstPlayer = hypothesisParams?.firstPlayer || tutorialScenario?.firstPlayer;
    if (specifiedFirstPlayer) {
        firstPlayerId = specifiedFirstPlayer === 'HUMAN' ? HUMAN_PLAYER_ID : NPC_PLAYER_ID;
        console.log(`[GAME] Fixed First Player: ${firstPlayerId}`);
    } else {
        firstPlayerId = playerIds[Math.floor(Math.random() * playerIds.length)];
    }

    const secondPlayerId = firstPlayerId === PlayerId.PLAYER1 ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
    gameState.current_turn = firstPlayerId;
    gameState.first_player = firstPlayerId;

    const firstPlayerName = gameState.players[firstPlayerId].name;
    const secondPlayerName = gameState.players[secondPlayerId].name;
    
    gameState.animation_queue.push({
        effect: {
            effect_type: 'TURN_ORDER_DECISION',
            args: {
                first_player: firstPlayerName,
                second_player: secondPlayerName,
                first_player_id: firstPlayerId,
                second_player_id: secondPlayerId
            }
        },
        sourceCard: null
    });
    
    if (!hypothesisParams) {
        gameState.players[firstPlayerId].consciousness += FIRST_PLAYER_CONSCIOUSNESS_MODIFIER;
    }
    
    gameState.game_log.push({
        id: `game_start_${Date.now()}`,
        timestamp: Date.now(),
        type: "game_start",
        message: getUILoc('MESSAGES.GAME_START_LOG', { name: gameState.players[firstPlayerId].name }),
        details: {
            first_player_id: firstPlayerId,
            first_player_name: gameState.players[firstPlayerId].name,
            debuff_applied: hypothesisParams ? 0 : FIRST_PLAYER_CONSCIOUSNESS_MODIFIER
        }
    });

    // マリガン無しの場合、即座に1ターン目の開始処理（ドロー等）を予約
    const skipMulligan = hypothesisParams?.enableMulligan === false || (tutorialScenario && tutorialScenario.enableMulligan === false);
    if (skipMulligan) {
        console.log('[GAME] Skipping Mulligan. Initializing Turn 1 Draw.');
        gameState.effect_queue.push([{
            effect_type: EffectType.DRAW_CARD,
            args: { player_id: firstPlayerId }
        }, null]);
        
        const ownerEventArgs = { player_id: firstPlayerId, target_player_id: firstPlayerId };
        const opponentEventArgs = { player_id: firstPlayerId, target_player_id: secondPlayerId };
        gameState.effect_queue.push([{ effect_type: TriggerType.START_TURN_OWNER, args: ownerEventArgs }, null]);
        gameState.effect_queue.push([{ effect_type: TriggerType.START_TURN_OPPONENT, args: opponentEventArgs }, null]);
    }

    return gameState;
};

export const performMulligan = (gameState, playerId, selectedCardIds) => {
    return produce(gameState, draftState => {
        const player = draftState.players[playerId];
        if (!player) return;

        draftState.mulligan_state[playerId].status = 'decided'; // Update status regardless
        draftState.mulligan_state[playerId].count = selectedCardIds.length; // Store count

        // If no cards to mulligan, just update status and return
        if (!selectedCardIds || selectedCardIds.length === 0) {
            return;
        }

        // 1. 手札からマリガン対象のカードを隔離
        const cardsToReturn = [];
        const remainingHand = [];
        player.hand.forEach(card => {
            if (selectedCardIds.includes(card.instance_id)) {
                cardsToReturn.push(card);
            } else {
                remainingHand.push(card);
            }
        });

        // 2. 新しいカードをデッキからドロー
        const numToDraw = cardsToReturn.length;
        const newCards = player.deck.splice(0, numToDraw);
        
        // Add a flag to the new cards
        newCards.forEach(card => {
            card.isNew = true;
            card.location = 'hand';
        });
        
        // 新しい手札をセット
        player.hand = [...remainingHand, ...newCards];

        // 3. 隔離したカードをデッキに戻してシャッフル
        cardsToReturn.forEach(card => {
            card.location = 'deck';
        });
        player.deck.push(...cardsToReturn);
        player.deck = shuffle(player.deck);

        // 4. マリガン状態を更新 (status and count already updated)
    });
};

export const resolveMulliganPhase = (gameState) => {
    const { mulligan_state } = gameState;
    if (mulligan_state[HUMAN_PLAYER_ID].status === 'decided' && mulligan_state[NPC_PLAYER_ID].status === 'decided') {
        // startTurn will produce the new state, including setting the phase to START_TURN
        return startTurn(gameState);
    }
    return gameState;
};


export const playCard = (gameState, playerId, cardInstanceId, options = {}) => {
    return produce(gameState, draftState => {
        const player = draftState.players[playerId];
        
        if (draftState.current_turn !== playerId) {
            return;
        }

        const cardIndex = player.hand.findIndex(c => c.instance_id === cardInstanceId);
        if (cardIndex === -1) {
            return;
        }
        
        const card = player.hand[cardIndex];

        if (getEffectiveScale(player, draftState) < card.required_scale) {
            return;
        }

        // '原始共産制' のIDは 22
        if (player.ideology && player.ideology.id === 22 && card.card_type === 1) {
            return;
        }

        const playerActionEffect = {
            effect_type: EffectType.PLAYER_ACTION,
            args: {
                player_id: playerId,
                action_type: 'play_card',
                card_id: cardInstanceId,
                ...options
            }
        };
        draftState.effect_queue.push([playerActionEffect, card]);
    });
};

export const endTurn = (gameState) => {
    return produce(gameState, draftState => {
        const currentPlayerId = draftState.current_turn;
        const currentPlayer = draftState.players[currentPlayerId];

        // presentationControllerへの直接参照を削除し、animation_queueにデータを追加
        draftState.animation_queue.push({
            effect: {
                effect_type: 'TURN_END',
                args: {
                    player_name: currentPlayer.name,
                    player_id: currentPlayerId
                }
            },
            sourceCard: null // 特定のカードに紐づかないためnull
        });

        draftState.turn_end_state = 'processing_effects';
        draftState.processing_status.is_processing_turn_end = true;
        draftState.processing_status.pending_turn_transition = true;

        const opponentPlayerId = (currentPlayerId === PlayerId.PLAYER1) ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
        const ownerEventArgs = { player_id: currentPlayerId, target_player_id: currentPlayerId };
        const opponentEventArgs = { player_id: currentPlayerId, target_player_id: opponentPlayerId };
        draftState.effect_queue.push([{ effect_type: TriggerType.END_TURN_OWNER, args: ownerEventArgs }, null]);
        draftState.effect_queue.push([{ effect_type: TriggerType.END_TURN_OPPONENT, args: opponentEventArgs }, null]);
    });
};

export const _proceedToNextTurn = (gameState) => {
    let currentGameState = gameState;

    const nextState = produce(currentGameState, draftState => {
        // Record history for the turn that just ended
        const playerIdeology = draftState.players[HUMAN_PLAYER_ID].ideology;
        const npcIdeology = draftState.players[NPC_PLAYER_ID].ideology;

        draftState.turnHistory.push({
            turnNumber: draftState.turn_number,
            playerConsciousness: draftState.players[HUMAN_PLAYER_ID].consciousness,
            playerScale: draftState.players[HUMAN_PLAYER_ID].scale,
            npcConsciousness: draftState.players[NPC_PLAYER_ID].consciousness,
            npcScale: draftState.players[NPC_PLAYER_ID].scale,
            playerIdeologies: playerIdeology ? [playerIdeology.id] : [],
            npcIdeologies: npcIdeology ? [npcIdeology.id] : [],
        });

        const currentPlayerId = draftState.current_turn;
        const nextPlayerId = (currentPlayerId === PlayerId.PLAYER1) ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
        
        const currentPlayer = draftState.players[currentPlayerId];
        currentPlayer.cards_played_this_turn = 0;
        currentPlayer.redevelopment_used = false; // Reset redevelopment plan usage
        
        draftState.turn_end_state = 'ready_for_next_turn';
        draftState.processing_status.is_processing_turn_end = false;
        draftState.processing_status.pending_turn_transition = false;
        draftState.processing_status.awaiting_input_for = null;
        
        draftState.current_turn = nextPlayerId;
        
        if (currentPlayerId === draftState.first_player) {
            draftState.turn_in_round = 2;
        } else {
            draftState.round_number += 1;
            draftState.turn_in_round = 1;
        }
        
        draftState.turn_number = (draftState.turn_number || 1) + 1;
    });

    return startTurn(nextState);
};

export const startTurn = (gameState) => {
    return produce(gameState, draftState => {
        // Clean up isNew flag from all cards from previous mulligan phase
        Object.values(draftState.players).forEach(player => {
            player.hand.forEach(card => {
                if (card.isNew) {
                    delete card.isNew;
                }
            });
        });

        draftState.phase = GamePhase.START_TURN;
        const currentPlayerId = draftState.current_turn;
        const opponentPlayerId = (currentPlayerId === PlayerId.PLAYER1) ? PlayerId.PLAYER2 : PlayerId.PLAYER1;
        const currentPlayer = draftState.players[currentPlayerId];

        const isFirstPlayer = currentPlayerId === draftState.first_player;
        const turnOrder = isFirstPlayer ? getPlayerName('FIRST') : getPlayerName('SECOND');
        const roundNum = draftState.round_number || 1;
        
        const turnStartMessage = `${turnOrder} ${getUILoc('MESSAGES.TURN_START').replace('{round}', roundNum)} (${currentPlayer.name})`;
        
        // presentationControllerへの直接参照を削除し、animation_queueにデータを追加
        draftState.animation_queue.push({
            effect: {
                effect_type: 'TURN_START',
                args: {
                    player_name: currentPlayer.name,
                    player_id: currentPlayerId,
                    turn_number: roundNum
                }
            },
            sourceCard: null // 特定のカードに紐づかないためnull
        });
        
        if (!draftState.game_log) {
            draftState.game_log = [];
        }
        draftState.game_log.push({
            id: `turn_start_${Date.now()}`,
            timestamp: Date.now(),
            type: "turn_start",
            message: turnStartMessage,
            details: {
                player_id: currentPlayerId,
                player_name: currentPlayer.name,
                turn_order: turnOrder,
                round_number: roundNum,
                is_first_player: isFirstPlayer
            }
        });

        console.log(`🎮GAME_ANIM [main.js] Pushing DRAW_CARD effect for player ${currentPlayerId}, Turn: ${draftState.turn_number}`);
        draftState.effect_queue.push([{
            effect_type: EffectType.DRAW_CARD,
            args: { player_id: currentPlayerId }
        }, null]);

        const ownerEventArgs = { player_id: currentPlayerId, target_player_id: currentPlayerId };
        const opponentEventArgs = { player_id: currentPlayerId, target_player_id: opponentPlayerId };
        draftState.effect_queue.push([{ effect_type: TriggerType.START_TURN_OWNER, args: ownerEventArgs }, null]);
        draftState.effect_queue.push([{ effect_type: TriggerType.START_TURN_OPPONENT, args: opponentEventArgs }, null]);
    });
};

export const resolveInput = (gameState, chosenItems) => {
    const nextState = produce(gameState, draftState => {
        const { type, player_id, source_card_instance_id, destination_pile, source_piles, source_effect, position } = draftState.awaiting_input;

        draftState.awaiting_input = null;

        let sourceCard = null;
        
        sourceCard = draftState.all_card_instances[source_card_instance_id];
        
        if (!sourceCard) {
            for (const p of Object.values(draftState.players)) {
                sourceCard = [...p.field, p.ideology, ...p.hand, ...p.discard].find(c => c && c.instance_id === source_card_instance_id);
                if (sourceCard) break;
            }
        }

        if (!sourceCard) {
            return;
        }
        
        switch (type) {
            case 'CHOICE_CARD_TO_ADD': {
                const card_template_id = chosenItems;
                const addEffect = {
                    effect_type: EffectType.ADD_CARD_TO_GAME,
                    args: {
                        player_id: player_id,
                        card_template_id: card_template_id,
                        destination_pile: 'hand',
                    }
                };
                draftState.effect_queue.unshift([addEffect, sourceCard]);
                break;
            }
            case 'CHOICE_CARD_FROM_PILE': {
                const chosenCard = chosenItems;
                let original_pile = null;

                for (const pileName of source_piles) {
                    const pile = draftState.players[player_id][pileName];
                    if (pile && pile.find(c => c.instance_id === chosenCard.instance_id)) {
                        original_pile = pileName;
                        break;
                    }
                }

                if (original_pile) {
                    const moveEffect = {
                        effect_type: EffectType.MOVE_CARD,
                        args: {
                            player_id: player_id,
                            card_id: chosenCard.instance_id,
                            source_pile: original_pile,
                            destination_pile: destination_pile,
                            position: position,
                        }
                    };
                    draftState.effect_queue.unshift([moveEffect, sourceCard]);
                }
                break;
            }
            case 'CHOICE_CARDS_FOR_OPERATION': {
                const newEffect = {
                    effect_type: source_effect.effect_type,
                    args: {
                        ...source_effect.args,
                        selected_cards: chosenItems,
                    }
                };
                draftState.effect_queue.unshift([newEffect, sourceCard]);
                break;
            }
            case 'CHOICE_NUMBER': {
                const amount = chosenItems;
                const newEffect = {
                    effect_type: source_effect.effect_type,
                    args: {
                        ...source_effect.args,
                        amount: amount,
                    }
                };
                draftState.effect_queue.unshift([newEffect, sourceCard]);
                break;
            }
            case 'CHOICE_CARD_FOR_EFFECT':
            default: {
                const chosenCard = Array.isArray(chosenItems) ? chosenItems[0] : chosenItems;
                const newEffect = {
                    effect_type: source_effect.effect_type,
                    args: {
                        ...source_effect.args,
                        card_id: chosenCard.instance_id,
                    }
                };
                draftState.effect_queue.unshift([newEffect, sourceCard]);
                break;
            }
        }
    });
    
    let finalState = processEffects(nextState);
    
    if (finalState.awaiting_input) {
        return finalState;
    }
    
    if (finalState.processing_status.pending_turn_transition) {
        finalState = produce(finalState, draftState => {
            draftState.turn_end_state = 'processing_effects';
            draftState.processing_status.awaiting_input_for = null;
        });
        
        // Removed while loop here
        // The App.js useEffect will handle further processing
        //if (!finalState.awaiting_input && finalState.effect_queue.length === 0) {
        //    return _proceedToNextTurn(finalState);
        //}
    }
    
    return finalState;
};